<template>
  <el-dialog
    :before-close="onCloseDialog"
    :visible.sync="show"
    title="Hacer una llamada"
    class="phone-dialog"
  >
    <el-form
      ref="callSomeoneForm"
      :model="formData"
      :rules="rules"
      label-position="top"
      size="mini"
    >
      <el-row>
        <el-col :span="24">
          <el-form-item prop="phone">
            <el-autocomplete
              v-model="formData.phone"
              :fetch-suggestions="querySearch"
              placeholder="Buscar en la agenda o escribir número de teléfono"
              class="phone-input"
              size="mini"
              @select="handleSelect"
            >
              <template #default="{ item }">
                <div class="suggestion-item">
                  <el-tag size="mini" :type="item.online ? 'success' : 'danger'" class="lic-badge">
                    <span v-html="highlightMatch(item.lic, formData.phone)"></span>
                  </el-tag>
                  <div class="item-content">
                    <span class="name" v-html="highlightMatch(item.name, formData.phone)"></span>
                  </div>
                  <div class="right-content">
                    <span class="phone" v-html="highlightMatch(item.phone, formData.phone)"></span>
                  </div>
                </div>
              </template>
            </el-autocomplete>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row v-if="isMultiServiceWg">
        <el-col :span="24">
          <el-form-item label="Servicio" prop="callerId">
            <el-select v-model="formData.callerId" class="service-select" size="mini">
              <el-option
                v-for="item in providedServices"
                :key="item.phone"
                :label="`${item.service} - ${item.phone}`"
                :value="item.phone"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row class="mt-2" justify="right" type="flex">
        <el-button class="cancel-btn" size="mini" @click="onCloseDialog">Cerrar</el-button>
        <el-button type="primary" class="call-btn" size="mini" @click="call">
          <i class="fas fa-phone"></i> Llamar
        </el-button>
      </el-row>
    </el-form>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { getConfigByName } from "@/api/operations/config.ts";
import { WORKGROUPS_WITH_MULTIPLE_SERVICES } from "@/utils/constants";
import { useStore } from "@/utils/useStore";
import { getAgenda } from "../../api/operations/taxis";

interface FormData {
  phone: string;
  callerId: string;
}

interface ProvidedService {
  phone: string;
  service: string;
}

interface TaxiAgenda {
  lic: string;
  name: string;
  phone: string;
  online: boolean;
}

interface PhoneNumberConfig {
  [key: string]: {
    serviceProvided: string;
  };
}

const store = useStore();

const agenda = ref<TaxiAgenda[]>([]);
const callSomeoneForm = ref();
const formData = ref<FormData>({
  phone: "",
  callerId: "",
});

const providedServices = ref<ProvidedService[]>([]);

const rules = {
  phone: [
    {
      required: true,
      message: "Por favor, escribe un teléfono",
      trigger: "blur",
    },
  ],
};

// Computed properties
const defaultWorkgroup = computed(() => store.getters["app/defaultWorkgroup"]);
const show = computed(() => store.getters["callSomeoneDialog/show"]);
const phone = computed(() => store.getters["callSomeoneDialog/phone"]);

const isMultiServiceWg = computed(() =>
  WORKGROUPS_WITH_MULTIPLE_SERVICES.includes(defaultWorkgroup.value?.id)
);

// Watch for phone changes
watch(phone, (newVal) => {
  if (newVal) {
    formData.value.phone = newVal;
  }
});

// Methods
const callOpToPhone = (data: { to: string; phoneExt: string; callerId: string; idWg: string }) =>
  store.dispatch("pbxCalls/callOpToPhone", data);
const getPhoneExt = () => store.dispatch("clientUserRegisterData/getPhoneExt");

const highlightMatch = (text: string, query: string) => {
  if (!query) return text;
  const regex = new RegExp(`(${query})`, "gi");
  return text.replace(regex, '<span class="highlight">$1</span>');
};

const querySearch = (queryString: string, cb: (data: TaxiAgenda[]) => void) => {
  if (!queryString || queryString.trim() === "") {
    // Return empty array if no search query
    return cb([]);
  }

  // Split results into exact matches and partial matches
  const exactMatches: TaxiAgenda[] = [];
  const partialMatches: TaxiAgenda[] = [];

  agenda.value.forEach((item) => {
    const matchesLic = item.lic === queryString;
    const matchesPhone = item.phone === queryString;
    const matchesName = item.name.toLowerCase().includes(queryString.toLowerCase());
    const matchesPartialLic = item.lic.includes(queryString);
    const matchesPartialPhone = item.phone?.includes(queryString) || false;

    if (matchesLic || matchesPhone) {
      exactMatches.push(item);
    } else if (matchesName || matchesPartialLic || matchesPartialPhone) {
      partialMatches.push(item);
    }
  });

  // Sort both arrays by license number
  const sortByLic = (a: TaxiAgenda, b: TaxiAgenda) => {
    const licA = parseInt(a.lic) || 0;
    const licB = parseInt(b.lic) || 0;
    return licA - licB;
  };

  const sortedExactMatches = exactMatches.sort(sortByLic);
  const sortedPartialMatches = partialMatches.sort(sortByLic);

  // Combine results with exact matches first
  cb([...sortedExactMatches, ...sortedPartialMatches]);
};

const handleSelect = (item: TaxiAgenda) => {
  formData.value.phone = item.phone;
};

const call = async () => {
  const phoneExt = await getPhoneExt();
  if (!phoneExt) return false;

  const form = callSomeoneForm.value;
  if (!form) return false;

  form.validate(async (valid: boolean) => {
    if (valid) {
      const data = {
        to: formData.value.phone,
        phoneExt: phoneExt,
        callerId: formData.value.callerId || providedServices.value[0].phone,
        idWg: defaultWorkgroup.value.id,
      };
      await callOpToPhone(data);
      onCloseDialog();
    } else {
      return false;
    }
  });
};

const onCloseDialog = () => {
  if (callSomeoneForm.value) {
    callSomeoneForm.value.resetFields();
  }
  store.commit("callSomeoneDialog/setPhone", "");
  store.commit("callSomeoneDialog/setShow", false);
};

// Lifecycle hooks
onMounted(async () => {
  const { phoneNumbers } = (await getConfigByName(defaultWorkgroup.value.id, "pbx")) as {
    phoneNumbers: PhoneNumberConfig;
  };
  agenda.value = await getAgenda(defaultWorkgroup.value.id);
  providedServices.value = Object.entries(phoneNumbers || {}).map(([key, value]) => ({
    phone: key,
    service: value.serviceProvided,
  }));
});
</script>

<style lang="scss" scoped>
.phone-dialog {
  ::v-deep .el-dialog {
    max-width: 480px;
    border-radius: 8px;
    overflow: hidden;

    &__header {
      padding: 12px 16px;
      background: #f5f7fa;
      border-bottom: 1px solid #e4e7ed;
    }

    &__body {
      padding: 16px;
    }
  }
}

.phone-input {
  width: 100%;

  ::v-deep .el-input__inner {
    height: 32px;
    font-size: 14px;
    border-radius: 4px;
  }

  ::v-deep .el-autocomplete-suggestion {
    min-width: 400px !important;
  }
}

.service-select {
  width: 100%;
}

.suggestion-item {
  display: flex;
  align-items: center;
  padding: 6px 0;
  font-size: 13px;
  gap: 8px;

  .lic-badge {
    flex-shrink: 0;
    min-width: 60px;
    text-align: center;

    :deep(.highlight) {
      background-color: #ffd04b;
      padding: 0 2px;
      border-radius: 2px;
    }
  }

  .item-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .right-content {
    flex-shrink: 0;
    text-align: right;
    min-width: 100px;
  }

  .name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .phone {
    color: #909399;
    white-space: nowrap;
  }

  :deep(.highlight) {
    background-color: #ffd04b;
    padding: 0 2px;
    border-radius: 2px;
  }
}

.mt-2 {
  margin-top: 12px;
}

.cancel-btn {
  margin-right: 8px;
}

.call-btn {
  i {
    margin-right: 4px;
  }
}
</style>
